import {
  createSlice,
  createAsyncThunk,
  miniSerializeError,
  SerializedError,
} from "@reduxjs/toolkit";
import { LOADING_STATE } from "../types/constants";
import authService from "src/services/authService";
import auditModuleService from "src/services/auditModuleService";
import { canAccessVlmAudit } from "src/utils/permissionUtil";
import {
  CPT_AUDITOR_LDAP_GROUP,
  GENERAL_USER_LDAP_GROUP,
  CPT_AUDITOR_ADMIN_LDAP_GROUP,
} from "src/constants";

export const SIMULATE_GENERAL_USER_KEY = "X-Simulate-General-User";

export type UserState = {
  token: string;
  alias: string;
  lastName: string;
  firstName: string;
  email: string;
  ldap: string[];
  userLoading: LOADING_STATE;
  authError: SerializedError | null;
  hasPermission: boolean;
  pageLastLoadTimestamp: number;
  isLoading: boolean;
  // Audit permissions-group designation, resolved server-side (GET /audit_access/me).
  // Not derivable from LDAP alone: Program Owner is a registry-only role.
  auditPermissionRole: string | null;
  // False when the caller has no AuditPermissionMember row ("not a member" message).
  auditIsMember: boolean;
  canManageAuditConfig: boolean;
  // Use cases the caller owns (per-use-case "Admin"): the ones a Program Owner may
  // configure and staff. The server returns every use case for an Admin.
  ownedUseCaseIds: string[];
  // Every use case the caller may see (Admin: all active); empty -> "no use cases" message.
  accessibleUseCaseIds: string[];
  // Loading state of the role lookup, so routes can wait instead of bouncing.
  auditPermissionLoading: LOADING_STATE;
};

const initialState: UserState = {
  token: "",
  alias: "",
  lastName: "",
  firstName: "",
  email: "",
  ldap: [],
  userLoading: "pending",
  authError: null,
  hasPermission: true,
  pageLastLoadTimestamp: 0,
  isLoading: false,
  auditPermissionRole: null,
  auditIsMember: false,
  canManageAuditConfig: false,
  ownedUseCaseIds: [],
  accessibleUseCaseIds: [],
  auditPermissionLoading: "idle",
};

/**
 * get the authenticated user
 * @returns {Object}
 */
export const getUser = createAsyncThunk("user/getUser", async () => {
  return authService.login();
});

/**
 * Resolve the caller's audit permissions-group designation (Admin / Program
 * Owner / Auditor). Program Owner is a registry-only role, so it cannot be
 * derived from the JWT's LDAP groups — it must come from the server. Non-audit
 * users (or any failure) resolve to "no config access", which fails closed.
 */
export const getAuditPermission = createAsyncThunk(
  "user/getAuditPermission",
  async () => {
    const permission = await auditModuleService.getMyPermission();
    // Accessible use cases for any member. Both endpoints use the same gate, so this
    // cannot 403 once /me succeeded; any other failure keeps the role with no use cases.
    let useCases = { useCaseRoles: {} as Record<string, string> };
    if (permission.isMember) {
      try {
        useCases = await auditModuleService.getMyUseCases();
      } catch {
        // Keep the resolved role; the member just sees no use cases until reload.
      }
    }
    return { ...permission, ...useCases };
  },
  {
    // Only call /audit_access/me for users who can see the VLM audit module
    // (temporary beta-tester guard). /me and /my_use_cases return 200 for
    // non-members (isMember=false), so they never trip the global 403 handler.
    condition: (_, { getState }) => {
      const { ldap, alias } = (getState() as { user: UserState }).user;
      return canAccessVlmAudit(ldap, alias);
    },
  },
);

const { reducer, actions } = createSlice({
  name: "user",
  initialState,
  reducers: {
    loginAsGeneralUser: (state) => {
      localStorage.setItem(SIMULATE_GENERAL_USER_KEY, state.alias);
      window.location.reload();
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getUser.pending, (state) => {
      state.userLoading = "pending";
    });
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(getUser.fulfilled, (state, { payload }) => {
      const { alias, token, email, firstName, lastName, ldap } = payload;
      state.token = token;
      state.alias = alias;
      const simulateUser = localStorage.getItem("X-Simulate-General-User");
      if (typeof simulateUser === "string" && simulateUser) {
        state.alias = simulateUser;
        // state.ldap = [GENERAL_USER_LDAP_GROUP];
        state.ldap = [
          GENERAL_USER_LDAP_GROUP,
          CPT_AUDITOR_LDAP_GROUP,
          CPT_AUDITOR_ADMIN_LDAP_GROUP,
        ];
      } else {
        state.alias = alias;
        state.ldap = ldap;
      }
      state.email = email;
      state.firstName = firstName;
      state.lastName = lastName;
      state.userLoading = "fulfilled";
      state.pageLastLoadTimestamp = Date.now();
      if (state.ldap.length === 0) {
        state.hasPermission = false;
      }
      // TODO: add JWT token to api header
    });
    builder.addCase(getUser.rejected, (state, { error }) => {
      state.authError = miniSerializeError(error);
      state.userLoading = "rejected";
      state.hasPermission = false;
    });
    builder.addCase(getAuditPermission.pending, (state) => {
      state.auditPermissionLoading = "pending";
    });
    builder.addCase(getAuditPermission.fulfilled, (state, { payload }) => {
      state.auditPermissionRole = payload.role;
      state.auditIsMember = payload.isMember;
      state.canManageAuditConfig = payload.canManageMembers;
      state.ownedUseCaseIds = Object.keys(payload.useCaseRoles).filter(
        (id) => payload.useCaseRoles[id] === "Admin",
      );
      state.accessibleUseCaseIds = Object.keys(payload.useCaseRoles);
      state.auditPermissionLoading = "fulfilled";
    });
    builder.addCase(getAuditPermission.rejected, (state) => {
      // Fail closed: a non-audit user (403) or any error means no config access.
      state.auditPermissionRole = null;
      state.auditIsMember = false;
      state.canManageAuditConfig = false;
      state.ownedUseCaseIds = [];
      state.accessibleUseCaseIds = [];
      state.auditPermissionLoading = "rejected";
    });
  },
});

export const { loginAsGeneralUser } = actions;

export default reducer;
